import { Dialog } from '@cycling-coach/web/components/Dialog';
import { Button, ErrorNote, Field } from '@cycling-coach/web/components/ui';
import { errorMessage } from '@cycling-coach/web/lib/queries';
import { useId, useState, type FormEvent } from 'react';
import { useHostedConnections, useRemoveZwift, useSaveZwift } from '../lib/hosted-api';

const INPUT = 'min-h-[42px] min-w-0 rounded-lg border border-line-strong bg-surface px-3 text-ink mobile:min-h-[44px]';

/**
 * Connecting a Zwift login on hosted (HOSTED-DECISIONS H2). Only flagged accounts see the Zwift row at
 * all (DECISIONS #54). The login is stored encrypted and used read-only; saving signs in once, so a wrong
 * password shows here straight away.
 */
export function ZwiftConnectAction() {
  const conn = useHostedConnections();
  const [open, setOpen] = useState(false);
  // A stored login that broke is fixed from `ZwiftManage` (Change login).
  if (!conn.data?.zwift.allowed || conn.data.zwift.connected) return null;
  return (
    <>
      <Button size="sm" variant="primary" onClick={() => setOpen(true)}>
        Connect Zwift
      </Button>
      {open && <ZwiftLoginDialog onClose={() => setOpen(false)} />}
    </>
  );
}

/** While a login is stored: which one, and Change login / Disconnect. */
export function ZwiftManage() {
  const conn = useHostedConnections();
  const remove = useRemoveZwift();
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  if (!conn.data?.zwift.connected) return null;
  return (
    <div className="flex basis-full flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-muted">
      {confirming ? (
        <>
          <span>Disconnect Zwift? Your synced zFTP, zMAP and weight stay; they just stop updating.</span>
          <Button size="sm" variant="danger" disabled={remove.isPending} onClick={() => remove.mutate(undefined, { onSettled: () => setConfirming(false) })}>
            {remove.isPending ? 'Disconnecting…' : 'Disconnect'}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setConfirming(false)}>
            Cancel
          </Button>
        </>
      ) : (
        <>
          <span>Signed in as {conn.data.zwift.hint ?? 'your Zwift account'}</span>
          <Button size="sm" variant="ghost" onClick={() => setOpen(true)}>
            Change login
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setConfirming(true)}>
            Disconnect
          </Button>
        </>
      )}
      {remove.error && <ErrorNote>{errorMessage(remove.error)}</ErrorNote>}
      {open && <ZwiftLoginDialog onClose={() => setOpen(false)} />}
    </div>
  );
}

function ZwiftLoginDialog({ onClose }: { onClose: () => void }) {
  const id = useId();
  const save = useSaveZwift();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [understood, setUnderstood] = useState(false);
  const rejected = save.data?.status === 'error' ? save.data.lastError : undefined;

  function submit(e: FormEvent) {
    e.preventDefault();
    save.mutate({ username, password }, { onSuccess: (status) => status.status === 'connected' && onClose() });
  }

  return (
    <Dialog
      title="Connect Zwift"
      width={460}
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form={`${id}-form`} variant="primary" disabled={!understood || !username || !password || save.isPending}>
            {save.isPending ? 'Signing in…' : 'Connect'}
          </Button>
        </div>
      }
    >
      <form id={`${id}-form`} onSubmit={submit} className="flex flex-col gap-4">
        <p className="m-0 text-sm text-muted">
          Your zFTP, zMAP and weight sync from Zwift every 6 hours, and “Get more events” can load about a week of events. Nothing is ever posted to
          your Zwift account.
        </p>
        <Field label="Zwift email" htmlFor={`${id}-user`}>
          <input id={`${id}-user`} type="email" autoComplete="off" required value={username} onChange={(e) => setUsername(e.target.value)} className={INPUT} />
        </Field>
        <Field label="Zwift password" htmlFor={`${id}-pass`} hint="Stored encrypted and used only to read your Zwift data.">
          <input id={`${id}-pass`} type="password" autoComplete="off" required value={password} onChange={(e) => setPassword(e.target.value)} className={INPUT} />
        </Field>
        <label className="flex items-start gap-2.5 rounded-[10px] bg-surface-2 p-3 text-[13px]">
          <input type="checkbox" checked={understood} onChange={(e) => setUnderstood(e.target.checked)} className="mt-0.5" />
          <span>
            I understand this uses Zwift’s <strong>unofficial</strong> API. It isn’t endorsed by Zwift and may breach Zwift’s terms of service; my
            account, my risk.
          </span>
        </label>
        {rejected && <ErrorNote>{rejected}</ErrorNote>}
        {save.error && <ErrorNote>{errorMessage(save.error)}</ErrorNote>}
      </form>
    </Dialog>
  );
}
